import React, { useState } from 'react';
import {
  Box, Button, IconButton, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography,
} from '@mui/material';
import ReactApexChart from 'react-apexcharts';
import Swal from 'sweetalert2';
import { Plus, AlertTriangle, ArrowDownRight, ArrowUpRight, PackagePlus, Pencil, Trash2 } from 'lucide-react';
import { PageHeader, StatusChip, Panel, StatStrip, FormDialog, LoadGate, PageShell, CHART_FONT } from './businessUi';
import { computeAllMovements, computeStockLevels, formatCurrency, formatNumber } from './erpData';
import { useErp } from './useErp';
import { ERP_KEYS } from '../../Services/app/erpservice';

const KEYS = { items: ERP_KEYS.inventory, manualMoves: ERP_KEYS.movements, production: ERP_KEYS.production, boms: ERP_KEYS.boms };
const CAT_COLORS = ['#7c5cff', '#0ea5e9', '#10b981', '#f59e0b', '#d946ef'];

const ITEM_FIELDS = [
  { name: 'code', label: 'Item Code', required: true, helper: 'Must be unique — e.g. RM-STEEL-50' },
  { name: 'name', label: 'Item Name', required: true },
  { name: 'category', label: 'Category', type: 'select', default: 'Raw Material', options: ['Raw Material', 'Tooling', 'Finished Goods', 'Consumable'] },
  { name: 'uom', label: 'Unit (UoM)', default: 'nos' },
  { name: 'warehouse', label: 'Warehouse', type: 'select', default: 'Main Store', options: ['Main Store', 'Tool Crib', 'Dispatch'] },
  { name: 'opening', label: 'Opening Stock', type: 'number', default: 0 },
  { name: 'reorder', label: 'Reorder Level', type: 'number', default: 0 },
  { name: 'rate', label: 'Rate (INR)', type: 'number', default: 0 },
];
// Same fields, but code is locked when editing (it's the key that ties movements to the item).
const EDIT_FIELDS = ITEM_FIELDS.map((f) => (f.name === 'code' ? { ...f, disabled: true, helper: 'Code cannot be changed' } : f));

export default function Inventory() {
  const { data, loading, error, save, reload } = useErp(KEYS);
  const [addOpen, setAddOpen] = useState(false);
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(null);

  const items = data?.items || [];
  const manualMoves = data?.manualMoves || [];
  const movements = computeAllMovements(manualMoves, data?.production || [], data?.boms || []);
  const levels = computeStockLevels(items, movements);

  const fail = () => Swal.fire({ icon: 'error', title: 'Save failed', text: 'Could not save to ThingsBoard. Please retry.', confirmButtonColor: '#5b3df5' });

  const handleAdd = (v) => save('items', [...items, v]).catch(fail);
  const handleEdit = (v) => save('items', items.map((it, i) => (i === editIndex ? { ...it, ...v } : it))).catch(fail);
  const handleDelete = (idx) => {
    const it = items[idx];
    Swal.fire({
      title: 'Delete this item?',
      html: `<b>${it.name}</b> (${it.code})<br/><span style="font-size:13px;color:#64748b">This removes the item master only.</span>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: 'text.disabled',
      confirmButtonText: 'Yes, delete it',
      allowOutsideClick: false,
    }).then((result) => {
      if (!result.isConfirmed) return;
      save('items', items.filter((_, i) => i !== idx))
        .then(() => Swal.fire({ icon: 'success', title: 'Deleted', timer: 1200, showConfirmButton: false }))
        .catch(fail);
    });
  };
  const handleReceive = (v) => {
    const qty = Number(v.qty) || 0;
    if (qty <= 0) { Swal.fire({ icon: 'warning', title: 'Invalid quantity', text: 'Enter a quantity greater than 0.', confirmButtonColor: '#5b3df5' }); return; }
    const mv = {
      date: v.date, code: v.code, type: v.direction, qty,
      ref: (v.note && v.note.trim()) || `ADJ-${manualMoves.length + 1}`,
      source: v.direction === 'IN' ? 'Receipt' : 'Issue',
    };
    save('manualMoves', [...manualMoves, mv]).catch(fail);
  };

  // Unique item list for the receive dropdown (codes may be duplicated in the master).
  const itemOptions = Array.from(new Map(items.map((i) => [i.code, i])).values())
    .map((i) => ({ value: i.code, label: `${i.code} — ${i.name}` }));
  const RECEIVE_FIELDS = [
    { name: 'code', label: 'Item', type: 'select', required: true, options: itemOptions },
    { name: 'direction', label: 'Movement', type: 'select', default: 'IN', options: [{ value: 'IN', label: 'Receive  (+ add to stock)' }, { value: 'OUT', label: 'Issue  (− remove from stock)' }] },
    { name: 'qty', label: 'Quantity', type: 'number', default: 0 },
    { name: 'date', label: 'Date', type: 'date', default: '2026-06-13' },
    { name: 'note', label: 'Reference / Note', helper: 'e.g. GRN-7701, or "opening top-up"' },
  ];

  const lowStock = items.filter((i) => levels[i.code] < i.reorder);
  const stockValue = items.reduce((s, i) => s + (levels[i.code] || 0) * i.rate, 0);

  const byCategory = {};
  items.forEach((i) => { byCategory[i.category] = (byCategory[i.category] || 0) + (levels[i.code] || 0) * i.rate; });
  const catLabels = Object.keys(byCategory);
  const catValues = catLabels.map((c) => Math.round(byCategory[c]));

  const donutOptions = {
    chart: { type: 'donut', fontFamily: CHART_FONT, dropShadow: { enabled: true, top: 4, blur: 8, opacity: 0.12 } },
    labels: catLabels,
    colors: CAT_COLORS,
    legend: { position: 'bottom', fontSize: '12px', markers: { radius: 12 } },
    dataLabels: { enabled: false },
    stroke: { width: 2 },
    plotOptions: { pie: { donut: { size: '70%', labels: { show: true, total: { show: true, label: 'Stock Value', fontSize: '12px', color: 'text.secondary', formatter: () => formatCurrency(stockValue) } } } } },
    tooltip: { y: { formatter: (v) => formatCurrency(v) } },
  };

  return (
    <PageShell>
      <PageHeader
        title="Inventory"
        subtitle="Transaction-driven stock — raw material, tooling, WIP & finished goods"
        actions={
          <>
            <Button variant="outlined" size="small" startIcon={<PackagePlus size={16} />} onClick={() => setReceiveOpen(true)} sx={{ textTransform: 'none' }} disabled={items.length === 0}>
              Receive / Adjust
            </Button>
            <Button variant="contained" size="small" startIcon={<Plus size={16} />} onClick={() => setAddOpen(true)}>Add Item</Button>
          </>
        }
      />

      <FormDialog open={addOpen} title="Add Inventory Item" fields={ITEM_FIELDS} onClose={() => setAddOpen(false)} onSubmit={handleAdd} submitLabel="Add Item" />
      <FormDialog open={editIndex !== null} title="Edit Inventory Item" fields={EDIT_FIELDS} initialValues={editIndex !== null ? items[editIndex] : undefined} onClose={() => setEditIndex(null)} onSubmit={handleEdit} submitLabel="Save Changes" />
      <FormDialog open={receiveOpen} title="Receive / Adjust Stock" fields={RECEIVE_FIELDS} onClose={() => setReceiveOpen(false)} onSubmit={handleReceive} submitLabel="Post Movement" />

      <LoadGate loading={loading} error={error} onRetry={reload}>
        <Box sx={{ mb: 3 }}>
          <StatStrip
            items={[
              { label: 'SKUs', value: String(items.length), tone: 'primary' },
              { label: 'Below Reorder', value: String(lowStock.length), tone: 'warning', hint: lowStock.length ? 'needs attention' : 'all healthy' },
              { label: 'Stock Value', value: formatCurrency(stockValue), tone: 'success' },
              { label: 'Movements', value: String(movements.length), tone: 'sky', hint: 'this period' },
            ]}
          />
        </Box>

        {items.length === 0 ? (
          <Panel title="Stock on Hand" accent="linear-gradient(90deg,#0ea5e9,#38bdf8)">
            <Typography sx={{ p: 4, textAlign: 'center', color: 'text.disabled', fontSize: 14 }}>
              No items yet. Click <strong>Add Item</strong> to create your first inventory record — it’s saved to ThingsBoard.
            </Typography>
          </Panel>
        ) : (
          <>
            <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', lg: '5fr 7fr' }, mb: 2.5 }}>
              <Box sx={{ display: 'grid', gap: 2.5, gridTemplateRows: 'auto auto' }}>
                <Panel title="Stock Value by Category" accent="linear-gradient(90deg,#7c5cff,#818cf8)">
                  <Box sx={{ px: 2, pb: 2 }}>
                    <ReactApexChart options={donutOptions} series={catValues} type="donut" height={260} />
                  </Box>
                </Panel>

                <Panel title="Reorder Alerts" subtitle={`${lowStock.length} item(s) below reorder level`} accent="linear-gradient(90deg,#f59e0b,#fbbf24)">
                  <Box sx={{ px: 2, pb: 2 }}>
                    {lowStock.length === 0 && <Typography sx={{ fontSize: 13, color: 'text.secondary', py: 2 }}>No items below reorder level.</Typography>}
                    {lowStock.map((i, idx) => (
                      <Box key={`${i.code}-${idx}`} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, borderBottom: '1px solid #f1f5f9' }}>
                        <Box sx={{ width: 34, height: 34, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(245,158,11,0.14)', color: '#b45309', flexShrink: 0 }}>
                          <AlertTriangle size={17} />
                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: 'text.primary' }}>{i.name}</Typography>
                          <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>{i.code} · {i.warehouse}</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#b45309' }}>{formatNumber(levels[i.code])} {i.uom}</Typography>
                          <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>reorder {formatNumber(i.reorder)}</Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Panel>
              </Box>

              <Panel title="Stock on Hand" subtitle="Live balance derived from the movement ledger" accent="linear-gradient(90deg,#0ea5e9,#38bdf8)">
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Code</TableCell>
                        <TableCell>Item</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell align="right">On Hand</TableCell>
                        <TableCell align="right">Value</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.map((i, idx) => {
                        const onHand = levels[i.code];
                        const low = onHand < i.reorder;
                        return (
                          <TableRow key={`${i.code}-${idx}`} hover>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{i.code}</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                              {i.name}
                              <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>{i.warehouse}</Typography>
                            </TableCell>
                            <TableCell><StatusChip label={i.category} tone="secondary" /></TableCell>
                            <TableCell align="right">
                              <Typography component="span" sx={{ fontSize: 14, fontWeight: low ? 700 : 500, color: low ? '#b45309' : 'inherit' }}>
                                {formatNumber(onHand)} {i.uom}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">{formatCurrency(onHand * i.rate)}</TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                              <IconButton size="small" onClick={() => setEditIndex(idx)} sx={{ color: '#5b3df5' }}><Pencil size={15} /></IconButton>
                              <IconButton size="small" onClick={() => handleDelete(idx)} sx={{ color: '#be123c' }}><Trash2 size={15} /></IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Panel>
            </Box>

            <Panel title="Stock Movement Feed" subtitle="Receipts, issues, dispatch & shop-floor consumption — on-hand is the running total" accent="linear-gradient(90deg,#10b981,#34d399)">
              <Box sx={{ px: 2, pb: 1.5 }}>
                {movements.length === 0 && <Typography sx={{ fontSize: 13, color: 'text.disabled', py: 2 }}>No stock movements yet. Use <strong>Receive / Adjust</strong> to add or correct stock.</Typography>}
                {movements.map((m, idx) => {
                  const item = items.find((i) => i.code === m.code);
                  const isIn = m.type === 'IN';
                  return (
                    <Box key={`${m.ref}-${m.code}-${idx}`} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.1, borderBottom: idx < movements.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                      <Box sx={{ width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, bgcolor: isIn ? 'rgba(16,185,129,0.14)' : 'rgba(244,63,94,0.12)', color: isIn ? '#047857' : '#be123c' }}>
                        {isIn ? <ArrowDownRight size={16} /> : <ArrowUpRight size={16} />}
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: 'text.primary' }}>
                          {item ? item.name : m.code} <Typography component="span" sx={{ fontSize: 11.5, fontFamily: 'monospace', color: 'text.disabled' }}>{m.code}</Typography>
                        </Typography>
                        <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>{m.date} · {m.ref}</Typography>
                      </Box>
                      <StatusChip label={m.source} tone="secondary" />
                      <Box sx={{ width: 110, textAlign: 'right', flexShrink: 0 }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: isIn ? '#047857' : '#be123c', fontVariantNumeric: 'tabular-nums' }}>
                          {isIn ? '+' : '−'}{formatNumber(m.qty, m.qty % 1 ? 2 : 0)} {item ? item.uom : ''}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Panel>
          </>
        )}
      </LoadGate>
    </PageShell>
  );
}
